import { Fragment, useEffect, useState } from 'react'
import { Calendar, MapPin, ListChecks, UserCog, FileText, FileSpreadsheet } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { Linha } from './LancamentoDepositos'

interface Lancamento { data: string; hora: number; dia: string; veicod: string; deposito: string; litros: number; motorista: string }
interface Grupo { codigo: string; nome: string; itens: Lancamento[]; lancamentos: number; litros: number }
interface Resultado { grupos: Grupo[]; total: { lancamentos: number; litros: number } }
interface Opcao { cod: string; des: string }

const fmtN = (v: number, c: number) => Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c })
const br = (d: string) => d.split('-').reverse().join('/')
const fmtHora = (h: number) => { const [hh, mm = '00'] = Number(h || 0).toFixed(2).split('.'); return `${hh.padStart(2, '0')}:${mm}` }
const hojeIso = () => new Date().toISOString().slice(0, 10)
const nomeGrupo = (g: Grupo) => g.nome ? `${g.codigo} — ${g.nome}` : `${g.codigo} — (sem cadastro)`

// Abastecimento interno por abastecedor (GeneXus WOABAINT → RABAINT), analítico ou sintético.
export default function RelatorioAbastecedor() {
  const [dataIni, setDataIni] = useState(hojeIso())
  const [dataFim, setDataFim] = useState(hojeIso())
  const [deposito, setDeposito] = useState('')
  const [abastecedorCod, setAbastecedorCod] = useState('')
  const [abastecedorNome, setAbastecedorNome] = useState('')
  const [tipoRel, setTipoRel] = useState<'S' | 'A'>('A')
  const [depositos, setDepositos] = useState<Opcao[]>([])
  const [res, setRes] = useState<Resultado | null>(null)
  const [resTipo, setResTipo] = useState<'S' | 'A'>('A')
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  useEffect(() => {
    api.get('/combustivel/depositos').then(r => setDepositos(r.data.map((p: any) => ({ cod: p.pracod, des: `${p.pracod} — ${p.pranom}` })))).catch(() => {})
  }, [])

  const params = () => ({ dataIni, dataFim, deposito, abastecedor: abastecedorCod, tipoRel })

  async function gerar() {
    setErro(''); setCarregando(true)
    try { setRes((await api.get('/combustivel/relatorios/abastecedor', { params: params() })).data); setResTipo(tipoRel) }
    catch (e: any) { setRes(null); setErro(e?.response?.data?.error || 'Erro ao gerar relatório.') }
    finally { setCarregando(false) }
  }

  async function exportar(formato: 'pdf' | 'excel') {
    setErro(''); setGerando(formato)
    try {
      const r = await api.get(`/combustivel/relatorios/abastecedor/${formato}`, { params: params(), responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') window.open(url, '_blank')
      else {
        const a = document.createElement('a'); a.href = url
        a.download = `abastecimento-por-abastecedor-${tipoRel === 'S' ? 'sintetico' : 'analitico'}.xlsx`
        a.click(); URL.revokeObjectURL(url)
      }
    } catch (e: any) {
      // Erro de validação vem como blob JSON.
      try { const t = await e?.response?.data?.text?.(); setErro(JSON.parse(t).error) } catch { setErro(formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.') }
    } finally { setGerando('') }
  }

  const th: React.CSSProperties = { padding: '0.4rem 0.45rem', fontSize: '0.66rem', fontWeight: 800, color: '#fff', background: '#1a6ea8', whiteSpace: 'nowrap', textAlign: 'left', position: 'sticky', top: 0, zIndex: 1 }
  const td: React.CSSProperties = { padding: '0.28rem 0.45rem', fontSize: '0.74rem', borderBottom: '1px solid #eef2f7', whiteSpace: 'nowrap', textAlign: 'left', fontVariantNumeric: 'tabular-nums' }
  const dir: React.CSSProperties = { ...td, textAlign: 'right' }
  const analitico = resTipo === 'A'
  const nCols = analitico ? 8 : 3

  const linhaTotal = (rotulo: string, lancamentos: number, litros: number, geral = false) => {
    const estilo: React.CSSProperties = { ...td, fontWeight: 800, color: geral ? '#fff' : '#1b3f7b', background: geral ? '#1b3f7b' : '#eff6ff', borderBottom: '2px solid #bfdbfe' }
    return analitico ? (
      <tr>
        <td colSpan={5} style={estilo}>{rotulo}</td>
        <td style={{ ...estilo, textAlign: 'right' }}>{fmtN(litros, 3)}</td>
        <td colSpan={2} style={estilo}>{lancamentos} lançamento(s)</td>
      </tr>
    ) : (
      <tr>
        <td style={estilo}>{rotulo}</td>
        <td style={{ ...estilo, textAlign: 'right' }}>{lancamentos}</td>
        <td style={{ ...estilo, textAlign: 'right' }}>{fmtN(litros, 3)}</td>
      </tr>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <UserCog size={18} color="#fff" />
            </span>
            <div>
              <h2>Abastecimento Interno por Abastecedor</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Abastecimentos nos depósitos agrupados por quem abasteceu</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Período" icon={Calendar}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input type="date" value={dataIni} onChange={e => setDataIni(e.target.value)} style={{ maxWidth: 170 }} />
              <span style={{ fontSize: '0.8rem', color: '#57606f' }}>a</span>
              <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={{ maxWidth: 170 }} />
            </div>
          </Linha>
          <Linha label="Depósito" icon={MapPin}>
            <select value={deposito} onChange={e => setDeposito(e.target.value)} style={{ maxWidth: 320 }}>
              <option value="">Todos</option>
              {depositos.map(p => <option key={p.cod} value={p.cod}>{p.des}</option>)}
            </select>
          </Linha>
          <Linha label="Abastecedor" icon={UserCog}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ minWidth: 300 }}>
                <LookupSelect endpoint="/combustivel/motoristas" codKey="motcod" descKey="motnome" tagKey="motcod"
                  codValue={abastecedorCod} descValue={abastecedorNome}
                  onSelect={(cod, desc) => { setAbastecedorCod(cod); setAbastecedorNome(desc) }}
                  placeholder="Todos" listWidth={320} />
              </div>
              {abastecedorCod
                ? <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }} onClick={() => { setAbastecedorCod(''); setAbastecedorNome('') }}>Todos</button>
                : <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todos</span>}
            </div>
          </Linha>
          <Linha label="Tipo Relatório" icon={ListChecks}>
            <select value={tipoRel} onChange={e => setTipoRel(e.target.value as 'S' | 'A')} style={{ maxWidth: 200 }}>
              <option value="A">Analítico</option>
              <option value="S">Sintético</option>
            </select>
          </Linha>
          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>{erro}</div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }} onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }} onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo">
            <div style={{ display: 'flex', gap: '1.2rem', flexWrap: 'wrap', fontSize: '0.8rem', color: '#475569', marginBottom: '0.7rem' }}>
              <span><b>{res.grupos.length}</b> abastecedor(es)</span>
              <span><b>{res.total.lancamentos}</b> lançamento(s)</span>
              <span>Litros: <b style={{ color: '#1b3f7b' }}>{fmtN(res.total.litros, 3)}</b></span>
            </div>
            <div style={{ overflow: 'auto', maxHeight: '70vh', border: '1px solid #bfdbfe', borderRadius: 8 }}>
              <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
                <thead>
                  <tr>
                    {analitico
                      ? ['Data', 'Hora', 'Dia', 'Veículo', 'Depósito', 'Litros', 'Nome Motorista', 'Abastecedor'].map(c => (
                        <th key={c} style={{ ...th, textAlign: c === 'Litros' ? 'right' : 'left' }}>{c}</th>))
                      : ['Abastecedor', 'Lançamentos', 'Litros'].map(c => <th key={c} style={{ ...th, textAlign: c === 'Abastecedor' ? 'left' : 'right' }}>{c}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {res.grupos.map(g => (
                    <Fragment key={g.codigo}>
                      {analitico && g.itens.map((i, k) => (
                        <tr key={k} style={{ background: k % 2 ? '#f5f9fd' : '#fff' }}>
                          <td style={{ ...td, color: '#1a6ea8' }}>{br(i.data)}</td>
                          <td style={{ ...td, color: '#7c3aed' }}>{fmtHora(i.hora)}</td>
                          <td style={td}>{i.dia}</td>
                          <td style={{ ...td, fontWeight: 700, color: '#1a6ea8' }}>{i.veicod}</td>
                          <td style={{ ...td, color: '#b91c1c' }}>{i.deposito}</td>
                          <td style={dir}>{fmtN(i.litros, 3)}</td>
                          <td style={{ ...td, color: '#7c3aed' }}>{i.motorista}</td>
                          <td style={{ ...td, color: '#1a6ea8' }}>{g.nome || g.codigo}</td>
                        </tr>
                      ))}
                      {linhaTotal(analitico ? `Total Abastecedor   ${nomeGrupo(g)}` : nomeGrupo(g), g.lancamentos, g.litros)}
                    </Fragment>
                  ))}
                  {res.grupos.length > 0 && linhaTotal('TOTAL DO PERÍODO', res.total.lancamentos, res.total.litros, true)}
                  {res.grupos.length === 0 && <tr><td colSpan={nCols} style={{ ...td, textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum lançamento no período.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
